import type { ReactElement } from 'react';

import chevronDownIcon from 'app/assets/icons/sidebar/chevron-down.svg';
import LockIcon from 'app/assets/icons/lock3.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

import useSidebarGroupOpen from '../hooks/useSidebarGroupOpen';
import type { SidebarItemClickHandler, SidebarNavItem } from '../types';

import SidebarCountBadge from './SidebarCountBadge';
import SidebarItem from './SidebarItem';

const TITLE_CLASSES = 'vtwo:text-white/55';
const FEATURED_TITLE_CLASSES =
  'vtwo:bg-linear-to-r vtwo:from-[#c1a9f6] vtwo:to-[#a8bdf6] vtwo:bg-clip-text vtwo:text-transparent';

interface SidebarGroupProps {
  group: SidebarNavItem;
  onItemClick: SidebarItemClickHandler;
}

export default function SidebarGroup({
  group,
  onItemClick,
}: SidebarGroupProps): ReactElement | null {
  const items = group.children?.filter((item) => item.display !== false) ?? [];
  const [open, setOpen] = useSidebarGroupOpen(group.id);
  const hiddenCount = open ? 0 : countOf(items);

  if (items.length === 0) return null;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="vtwo:flex vtwo:flex-col vtwo:gap-0.5"
    >
      <CollapsibleTrigger className="vtwo:group vtwo:flex vtwo:h-8 vtwo:w-full vtwo:cursor-pointer vtwo:items-center vtwo:gap-3 vtwo:rounded-lg vtwo:px-2 vtwo:text-left vtwo:hover:bg-[#2e3032]/60">
        <span
          className={cn(
            'vtwo:min-w-0 vtwo:flex-1 vtwo:truncate vtwo:text-sm vtwo:font-semibold vtwo:leading-[18px]',
            group.featured ? FEATURED_TITLE_CLASSES : TITLE_CLASSES
          )}
        >
          {group.title}
        </span>
        {hiddenCount > 0 && <SidebarCountBadge count={hiddenCount} />}
        {group.locked && <LockIcon className="vtwo:size-4 vtwo:shrink-0 vtwo:text-[#c57070]" />}
        <img
          className="vtwo:size-4 vtwo:shrink-0 vtwo:rotate-180 vtwo:brightness-0 vtwo:invert vtwo:opacity-55 vtwo:transition-transform vtwo:group-data-[state=closed]:rotate-0"
          src={chevronDownIcon}
          alt=""
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="vtwo:flex vtwo:flex-col vtwo:gap-0.5">
        {items.map((item) => (
          <SidebarItem key={item.id} item={item} onItemClick={onItemClick} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// A collapsed group carries its items' counts on its header, so they aren't hidden with them.
function countOf(items: SidebarNavItem[]): number {
  return items.reduce(
    (total, item) => total + (typeof item.badge?.title === 'number' ? item.badge.title : 0),
    0
  );
}
